import { ActivityBadge } from "@/components/ActivityBadge";
import { MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";

// Design System 2.0 (2026-10, Phase 5) — the ONE record-detail header shared by Client,
// Partner and Contact Detail (title/status/tags/health/activity/brief + actions row + "⋯"
// overflow menu). Generalizes the ~3 near-identical hand-rolled header blocks that used to
// live separately inside Clients.jsx/PartnerDetail.jsx/ContactDetail.jsx. Actions are passed
// as already-built elements (not a config schema) so each page keeps its own data-testids,
// styling and behaviour exactly — this component only owns the shared LAYOUT, never the
// actions' logic. Purge must only ever be reachable from `overflow` (ArchiveControls
// variant="menu"), never as a top-level button — see each page's usage.
export function RecordPageLayout({ icon: Icon, title, titleTestId, statusChip, tags = [], health, activityIso, activityTestId, brief, archived, archivedTestId, primaryAction, secondaryActions, overflow, testId, children }) {
  return (
    <div className="p-8 space-y-8" data-testid={testId}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            {Icon && <Icon className="h-6 w-6 text-gold-text shrink-0" />}
            <h1 className="font-editorial text-4xl tracking-tight text-ink" data-testid={titleTestId || (testId ? `${testId}-title` : undefined)}>{title}</h1>
            {archived && (
              <span className="rounded-full bg-tone-grey-bg px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-tone-grey-text" data-testid={archivedTestId || (testId ? `${testId}-archived-badge` : undefined)}>
                Archived
              </span>
            )}
          </div>
          {(statusChip || tags.length > 0 || health || activityIso !== undefined) && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {statusChip}
              {tags.filter(Boolean).map((t, i) => <RecordTag key={i}>{t}</RecordTag>)}
              {health}
              {activityIso !== undefined && <ActivityBadge iso={activityIso} testId={activityTestId || (testId ? `${testId}-activity` : undefined)} />}
            </div>
          )}
          {brief && <p className="text-sm text-ink-soft mt-2 max-w-2xl">{brief}</p>}
        </div>
        {(primaryAction || secondaryActions || overflow) && (
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {secondaryActions}
            {primaryAction}
            {overflow && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" aria-label="More actions" data-testid={testId ? `${testId}-overflow-btn` : undefined}
                    className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-8 w-8 hover:border-brand transition-colors">
                    <MoreHorizontal className="h-4 w-4 text-ink-soft" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 p-1.5">
                  {overflow}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

/** A tag (industry, type, agency flags, etc.) — a plain label, never a StatusChip/tone. */
export const RecordTag = ({ children, testId }) => (
  <span data-testid={testId} className="rounded-full bg-surface2 px-2 py-0.5 text-[11px] font-medium text-ink-soft">{children}</span>
);
